const EXPLICIT_TIME_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * API timestamp columns currently store UTC wall-clock values without timezone
 * metadata. Preserve explicit offsets, but treat the legacy offset-less shape as
 * UTC so the browser can convert the instant to the viewer's local timezone.
 */
export const normalizeUtcTimestamp = (value: string): string => {
  const trimmed = value.trim();
  return EXPLICIT_TIME_ZONE.test(trimmed) ? trimmed : `${trimmed}Z`;
};

export const parseUtcTimestamp = (
  value?: Date | string | null,
): Date | null => {
  if (!value) return null;

  const parsed =
    value instanceof Date
      ? new Date(value.getTime())
      : new Date(normalizeUtcTimestamp(value));

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Display only proven UTC comment instants, in the employee's saved zone. */
export const formatCommentTimestamp = (value: Date | string | null | undefined, timeZone = 'America/Chicago'): string => {
  const instant = parseUtcTimestamp(value);
  return instant ? instant.toLocaleString('en-US', { timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
};

/** Compare named-zone calendar days, not elapsed 24-hour durations across DST. */
export const formatActivityTimestamp = (value: Date | string | null | undefined, now: Date, timeZone = 'America/Chicago'): string => {
  const instant = parseUtcTimestamp(value);
  if (!instant) return 'N/A';
  const day = (date: Date) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(date);
    const number = (kind: string) => Number(parts.find(part => part.type === kind)?.value);
    // An ordinal for presentation comparison only; this never changes a stored date or instant.
    return { ordinal: Date.UTC(number('year'), number('month') - 1, number('day')) / 86400000, year: number('year') };
  };
  const today = day(now); const messageDay = day(instant);
  const difference = today.ordinal - messageDay.ordinal;
  const time = instant.toLocaleTimeString('en-US', { timeZone, hour: 'numeric', minute: '2-digit', hour12: true });
  if (difference === 0) return time;
  if (difference === 1) return `Yesterday ${time}`;
  if (difference > 1 && difference < 7) return `${instant.toLocaleDateString('en-US', { timeZone, weekday: 'short' })} ${time}`;
  return `${instant.toLocaleDateString('en-US', { timeZone, month: 'short', day: 'numeric', ...(messageDay.year !== today.year ? { year: 'numeric' as const } : {}) })}, ${time}`;
};
